import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';

const ChildrenTab = ({ classes, onAssignClassClick, refreshKey }) => {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingChildId, setEditingChildId] = useState(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    age: '',
    language: 'en',
  });

  useEffect(() => {
    loadChildren();
  }, [refreshKey]);

  const loadChildren = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/parent/children');
      setChildren(response.data);
    } catch (err) {
      console.error('Error loading children:', err);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateChild = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage('');

    if (!formData.username || (!formData.password && !editingChildId) || !formData.age) {
      setError('All fields are required');
      return;
    }

    const age = parseInt(formData.age);
    if (isNaN(age) || age < 1 || age > 17) {
      setError('Age must be between 1 and 17');
      return;
    }

    try {
      if (editingChildId) {
        const payload = {
          username: formData.username,
          age: age,
          language: formData.language || 'en',
        };

        if (showPasswordFields) {
          if (!formData.password || !formData.confirmPassword) {
            setError('Please enter and confirm the new password');
            return;
          }
          if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
          }
          if (formData.password !== formData.confirmPassword) {
            const mismatchMessage = 'New Password and Confirm New Password must match';
            setError(mismatchMessage);
            alert(mismatchMessage);
            return;
          }
          payload.password = formData.password;
        }

        await api.put(`/parent/children/${editingChildId}`, payload);
        setSuccessMessage('Child account updated successfully.');
      } else {
        await api.post('/parent/children', {
          username: formData.username,
          password: formData.password,
          age: age,
          language: formData.language || 'en',
        });
        setSuccessMessage('Child account created successfully.');
      }
      
      setFormData({ username: '', password: '', confirmPassword: '', age: '', language: 'en' });
      setShowCreateForm(false);
      setEditingChildId(null);
      setShowPasswordFields(false);
      loadChildren();
    } catch (err) {
      console.error('Error saving child:', err);
      setError(handleApiError(err));
    }
  };

  const handleEditChild = (childId) => {
    setSuccessMessage('');
    const child = children.find(c => c.id === childId);
    if (child) {
      setFormData({
        username: child.username,
        password: '',
        confirmPassword: '',
        age: child.age ? String(child.age) : '',
        language: child.language || 'en',
      });
      setEditingChildId(childId);
      setShowPasswordFields(false);
      setShowCreateForm(true);
    }
  };

  const handleCancelChildForm = () => {
    setSuccessMessage('');
    setFormData({ username: '', password: '', confirmPassword: '', age: '', language: 'en' });
    setEditingChildId(null);
    setShowPasswordFields(false);
    setShowCreateForm(false);
  };

  const handleDeleteChild = async (childId) => {
    if (!window.confirm('Are you sure you want to delete this child account? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/parent/children/${childId}`);
      setSuccessMessage('Child account deleted successfully.');
      loadChildren();
    } catch (err) {
      console.error('Error deleting child:', err);
      setError(handleApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="loading">Loading children...</div>
    );
  }

  return (
    <div className="children-list">
      {successMessage && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.9rem 1rem',
            borderRadius: '8px',
            background: 'rgba(34, 197, 94, 0.12)',
            border: '1px solid rgba(34, 197, 94, 0.35)',
            color: '#15803d',
            fontWeight: 500,
          }}
        >
          {successMessage}
        </div>
      )}
      {error && (
        <div className="error-message" style={{ marginBottom: '1rem', padding: '1rem', borderRadius: '8px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>Child Accounts</h2>
        <button
          className="btn-secondary"
          onClick={() => {
            setShowCreateForm(true);
            setEditingChildId(null);
            setShowPasswordFields(false);
            setFormData({ username: '', password: '', confirmPassword: '', age: '', language: 'en' });
            setError(null);
            setSuccessMessage('');
          }}
        >
          Create New Child
        </button>
      </div>

      {/* Child Form Modal */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={handleCancelChildForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div className="modal-header">
              <h2>{editingChildId ? 'Edit Child Account' : 'Create New Child Account'}</h2>
              <button
                className="modal-close-btn"
                onClick={handleCancelChildForm}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateChild} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="form-group">
                  <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Username</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Enter username"
                    required
                    minLength={3}
                    maxLength={50}
                    style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                  />
                </div>
                
                {!editingChildId && (
                  <div className="form-group">
                    <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      required
                      minLength={6}
                      style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                    />
                  </div>
                )}
                <div className="form-group" style={{ maxWidth: editingChildId ? '140px' : 'none' }}>
                  <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Age</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    placeholder="1-17"
                    required
                    min={1}
                    max={17}
                    style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                  />
                </div>
                
                {editingChildId && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div className="form-group">
                        <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Language</label>
                        <select
                          name="language"
                          value={formData.language}
                          onChange={handleInputChange}
                          className="language-select-form"
                          style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                        >
                          <option value="en">English</option>
                          <option value="sr">Serbian</option>
                          <option value="es">Spanish</option>
                          <option value="fr">French</option>
                          <option value="de">German</option>
                          <option value="it">Italian</option>
                          <option value="pt">Portuguese</option>
                          <option value="ru">Russian</option>
                          <option value="zh">Chinese</option>
                          <option value="ja">Japanese</option>
                          <option value="ko">Korean</option>
                          <option value="ar">Arabic</option>
                          <option value="hi">Hindi</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>
                          Password
                        </label>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            const next = !showPasswordFields;
                            setShowPasswordFields(next);
                            if (!next) {
                              setFormData((prev) => ({ ...prev, password: '', confirmPassword: '' }));
                            }
                          }}
                          style={{ width: '100%', padding: '0.75rem', fontSize: '0.9rem' }}
                        >
                          {showPasswordFields ? 'Cancel' : 'Change'}
                        </button>
                      </div>
                    </div>
                    {showPasswordFields && (
                      <>
                        <div className="form-group">
                          <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>
                            New Password
                          </label>
                          <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Enter new password"
                            minLength={6}
                            style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                          />
                        </div>
                        <div className="form-group">
                          <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            placeholder="Repeat new password"
                            minLength={6}
                            style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                          />
                          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                            <div style={{ marginTop: '0.25rem', color: '#dc2626', fontSize: '0.8rem' }}>
                              Passwords do not match
                            </div>
                          )}
                          {formData.password &&
                            formData.confirmPassword &&
                            formData.password === formData.confirmPassword && (
                              <div style={{ marginTop: '0.25rem', color: '#16a34a', fontSize: '0.8rem', fontWeight: 600 }}>
                                ✓ Passwords match
                              </div>
                            )}
                        </div>
                      </>
                    )}
                  </>
                )}
                
                {!editingChildId && (
                  <div className="form-group">
                    <label style={{ marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>Language</label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="language-select-form"
                      style={{ padding: '0.75rem', fontSize: '0.9rem', width: '100%' }}
                    >
                      <option value="en">English</option>
                      <option value="sr">Serbian</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="it">Italian</option>
                      <option value="pt">Portuguese</option>
                      <option value="ru">Russian</option>
                      <option value="zh">Chinese</option>
                      <option value="ja">Japanese</option>
                      <option value="ko">Korean</option>
                      <option value="ar">Arabic</option>
                      <option value="hi">Hindi</option>
                    </select>
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={handleCancelChildForm}
                    style={{ flex: 1, padding: '0.75rem', fontSize: '0.9rem' }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn" 
                    style={{ flex: 1, padding: '0.75rem', fontSize: '0.9rem' }}
                  >
                    {editingChildId ? 'Update Child' : 'Create Child'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {children.length > 0 ? (
        <div className="children-grid">
          {children.map((child) => {
            const assignedIds = child.assigned_subject_ids || [];
            return (
              <div key={child.id} className="child-card">
              <div className="child-header">
                <h3>{child.username}</h3>
                {child.age && <span className="age-badge">Age: {child.age}</span>}
              </div>
              <div className="child-stats">
                <div className="child-stat">
                  <span className="child-stat-label">Created:</span>
                  <span className="child-stat-value">
                    {child.created_at ? new Date(child.created_at).toLocaleDateString() : 'Unknown'}
                  </span>
                </div>
                <div className="child-stat" style={{ marginTop: '0.5rem' }}>
                  <span className="child-stat-label">Language:</span>
                  <span className="child-stat-value">
                    {child.language ? {
                      'en': 'English',
                      'sr': 'Serbian',
                      'es': 'Spanish',
                      'fr': 'French',
                      'de': 'German',
                      'it': 'Italian',
                      'pt': 'Portuguese',
                      'ru': 'Russian',
                      'zh': 'Chinese',
                      'ja': 'Japanese',
                      'ko': 'Korean',
                      'ar': 'Arabic',
                      'hi': 'Hindi'
                    }[child.language] || child.language : 'English'}
                  </span>
                </div>
              </div>
              {/* Assigned Subjects / legacy Assigned Classes */}
              {assignedIds.length > 0 && (
                <div style={{ 
                  marginTop: '0.75rem', 
                  paddingTop: '0.75rem', 
                  borderTop: '1px solid #e2e8f0' 
                }}>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    color: '#718096', 
                    marginBottom: '0.5rem',
                    fontWeight: 600
                  }}>
                    Assigned Subjects:
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '0.5rem' 
                  }}>
                    {classes
                      .filter(cls => assignedIds.includes(cls.id))
                      .map(cls => (
                        <span
                          key={cls.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            background: 'rgba(102, 126, 234, 0.1)',
                            color: '#667eea',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          {cls.emoji && <span>{cls.emoji}</span>}
                          {cls.name}
                        </span>
                      ))}
                  </div>
                </div>
              )}
              
              <div className="child-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button
                  className="btn-small"
                  style={{
                    background: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    borderColor: '#667eea',
                    flex: 1
                  }}
                  onClick={() => handleEditChild(child.id)}
                >
                  Edit
                </button>
                <button
                  className="btn-small"
                  style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    color: '#22c55e',
                    borderColor: '#22c55e',
                    flex: 1
                  }}
                  onClick={() => {
                    onAssignClassClick(child);
                  }}
                >
                  Assign Classes
                </button>
                <button
                  className="btn-small btn-delete"
                  onClick={() => handleDeleteChild(child.id)}
                  aria-label="Delete child account"
                  title="Delete child account"
                >
                  Delete
                </button>
              </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <p>No child accounts yet. Create your first child account to get started.</p>
        </div>
      )}
    </div>
  );
};

export default ChildrenTab;

